<template>
  <div class="p-4 lg:p-8">
    <div class="surface-card p-4 shadow-sm border border-iron rounded-lg">
      
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-2xl font-bold text-secondary">Materiais Exclusivos</h2>
          <p class="text-sm text-gray-500">Gerencie os arquivos, apostilas e mídias liberadas para os alunos.</p>
        </div>
        <Button 
          label="Novo Material" 
          icon="pi pi-plus" 
          @click="openNew" 
          class="bg-purple-suit-forced border-none px-4" 
        />
      </div>

      <LoadingDialog :visible="loading" />
      <template v-if="!loading">
        <div v-for="(item, index) of statusOptions" :key="index" class="mt-5">
          <template v-if="Array.isArray(materials[item.value]) && materials[item.value].length > 0">
            <p class="text-xl font-bold text-secondary pb-2">
              Materiais de nível: {{ translate(item.label, 'ucfirst') }} 
              <small>
                (Total: {{ Array.isArray(materials[item.value]) ? materials[item.value].length : 0 }})
              </small>
            </p>

            <MaterialListComponent
              :value="materials[item.value]" 
              :loading="loading" 
              :downloading-ids="downloadingIds"
              @edit="editMaterial"
              @delete="confirmDelete"
              @download="downloadFile"
            />

            <Divider />
          </template>
        </div>
      </template>
    </div>

    <Dialog 
      v-model:visible="materialDialog" 
      :style="{ width: '550px' }" 
      :header="selectedMaterial?.id ? 'Editar Material' : 'Novo Material Exclusivo'" 
      :modal="true" 
      class="p-fluid"
      :closeOnEscape="true"
    >
      <div class="flex flex-col gap-4 mt-2">
        
        <div class="field">
          <label for="name" class="font-semibold text-sm text-secondary mb-1 block">Nome do Material *</label>
          <InputText id="name" class="w-full" v-model="form.name" placeholder="Ex: Apostila de Conversação Avançada" required />
        </div>

        <div class="field">
          <label for="description" class="font-semibold text-sm text-secondary mb-1 block">Descrição</label>
          <Textarea id="description" class="w-full" v-model="form.description" rows="3" placeholder="Detalhes sobre o conteúdo do arquivo..." autoResize />
        </div>

        <div class="field">
          <label for="status" class="font-semibold text-sm text-secondary mb-1 block">Nível</label>
          <Select v-model="form.status" :options="statusOptions" optionLabel="label" optionValue="value"
            :placeholder="translate('select a level', 'ucfirst')" class="w-full">

            <template #value="slotProps">
                <div v-if="slotProps.value">
                  {{ translate(materialDictionary.getLabel(slotProps.value), 'ucfirst') }}
                </div>
                <span v-else>
                  {{ translate('no level selected', 'ucfirst') }}
                </span>
            </template>

            <template #option="slotProps">
                <span>{{ translate(slotProps.option.label, 'ucfirst') }}</span>
            </template>

          </Select>
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">
            Arquivo do Material <span v-if="!selectedMaterial?.id">*</span>
          </label>
          
          <div 
            class="flex flex-col gap-2 p-4 border border-dashed rounded-lg text-center transition-colors duration-200"
            :class="isDragging ? 'border-primary bg-blue-50/30' : 'border-iron bg-gray-50'"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onFileDrop"
          >
            <input 
              type="file" 
              ref="fileInput" 
              id="fileUpload" 
              class="hidden" 
              accept=".pdf,.txt,.mp3,.mp4,image/*,.pptx" 
              @change="onFileSelect"
            />
            <label for="fileUpload" class="cursor-pointer flex flex-col items-center gap-2">
              <i class="pi pi-cloud-upload text-gray-400" style="font-size: 2rem"></i>
              <span class="text-sm text-gray-600 font-medium">
                {{ selectedFile ? selectedFile.name : 'Clique ou arraste o arquivo aqui para selecionar' }}
              </span>
              <span class="text-xs text-gray-400">
                Formatos aceitos: PDF, Imagens, TXT, MP3 e MP4
              </span>
            </label>
          </div>
          
          <p v-if="selectedMaterial?.id && !selectedFile" class="text-xs text-gray-500 mt-1 italic">
            * Deixe em branco se não desejar substituir o arquivo atual.
          </p>
        </div>

      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="materialDialog = false" :disabled="submitting" />
          <Button :label="selectedMaterial?.id ? 'Salvar Alterações' : 'Cadastrar'" icon="pi pi-check" class="bg-purple-suit-forced border-none" @click="saveMaterial" :loading="submitting" />
        </div>
      </template>
    </Dialog>

    <ConfirmDialog></ConfirmDialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";
import { useToast, Select, Divider } from 'primevue';
import { showToast } from '@/composables/toast';
import MaterialService from '@/services/MaterialService'; 
import MaterialDictionary from '@/dictionary/MaterialDictionary';
import { translate } from '@/composables/translate';
import MaterialListComponent from '@/components/Material/MaterialListComponent.vue';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';

const toast = useToast();
const confirm = useConfirm();

const materials = ref({});
const loading = ref(false);
const materialDialog = ref(false);
const submitting = ref(false);
const isDragging = ref(false);

const selectedMaterial = ref(null);
const selectedFile = ref(null);
const fileInput = ref(null);

const downloadingIds = ref(new Set());
const materialDictionary = new MaterialDictionary();
const statusOptions = materialDictionary.getAll();

const form = ref({
  name: '',
  description: '',
  status: null
});

onMounted(() => {
  loadMaterials();
});

const loadMaterials = async () => {
  loading.value = true;

  try {
    const response = await MaterialService.get();

    const tempMaterials = {};
    for (let item of statusOptions) {
      tempMaterials[item.value] = [];
    }

    for (let material of response) {
      if (tempMaterials[material.status]) {
        tempMaterials[material.status].push(material);
      }
    }

    materials.value = tempMaterials;
    
  } catch (error) {
    showToast(toast, 'Erro ao carregar materiais.', 'error');
  } finally {
    loading.value = false;
  }
};

const openNew = () => {
  selectedMaterial.value = null;
  selectedFile.value = null;
  form.value = { name: '', description: '', status: null };
  materialDialog.value = true;
};

const editMaterial = (material) => {
  selectedMaterial.value = material;
  selectedFile.value = null;
  form.value = { 
    name: material.name, 
    description: material.description || '',
    status: material.status || null
  };
  materialDialog.value = true;
};

const onFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
  }
};

const onFileDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    selectedFile.value = files[0];
  }
};

const downloadFile = async (material) => {
  const response = await MaterialService.fetchDonwloadData(material.id);
  
  if (!response.success()) {
    showToast(toast, response.getMessage(), 'error');
    return;
  }

  const url = window.URL.createObjectURL(new Blob([response.getContent()]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', material.name + material.getExtension());
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const saveMaterial = async () => {
  if (!form.value.name.trim()) {
    showToast(toast, 'O campo nome é obrigatório', 'error');
    return;
  }

  if (!form.value.status || materialDictionary.getLabel(form.value.status) == '') {
    showToast(toast, 'É obrigatório selecionar um nível', 'error');
    return;
  }

  if (!selectedMaterial.value?.id && !selectedFile.value) {
    showToast(toast, 'Selecione um arquivo para upload', 'error');
    return;
  }

  submitting.value = true;

  const formData = new FormData();
  if (selectedMaterial.value?.id) {
    formData.append('id', selectedMaterial.value.id);
  }
  formData.append('name', form.value.name.trim());
  formData.append('description', form.value.description.trim());
  formData.append('status', form.value.status);

  if (selectedFile.value) {
    formData.append('file', selectedFile.value);
  }

  try {
    let response = await MaterialService.save(formData);

    if (response.success()) {
      showToast(toast, selectedMaterial.value?.id ? 'Material atualizado com sucesso!' : 'Material criado com sucesso!', 'success');
      materialDialog.value = false;
      loadMaterials();
    } else {
      showToast(toast, response.getMessage(), 'error');
    }
  } catch (error) {
    showToast(toast, 'Erro ao salvar o material.', 'error');
  } finally {
    submitting.value = false;
  }
};

const confirmDelete = (material) => {
  confirm.require({
    message: `Tem certeza que deseja excluir permanentemente o material "${material.name}"? Esta ação removerá o arquivo físico do servidor.`,
    header: 'Confirmação de Exclusão',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, excluir',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      loading.value = true;
      try {
        const response = await MaterialService.delete(material.id);
        if (response.success()) {
          showToast(toast, 'Material removido com sucesso.', 'success');
          loadMaterials();
        } else {
          showToast(toast, response.getMessage(), 'error');
        }
      } catch (error) {
        showToast(toast, 'Erro ao deletar o material.', 'error');
      } finally {
        loading.value = false;
      }
    }
  });
};
</script>